import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import BigNumber from 'bignumber.js';
import { OrderCardContext } from '../../../../../contexts/card/cardOrder/context';
import { currencies, exchange, REPORT_CARD_STATUS } from '../../../../../utilities/constants';
import { formatNumber, formatNumberWithComma } from '../../../../../utilities/formatHelpers';
import {
  CARD_MONAVATE_REPORT_LOST,
  CARD_MONAVATE_REPORT_STOLEN,
  CARD_ORDER_MONAVATE,
  USA_CARD_CARD_ORDER,
} from '../../../mutations';
import { FLOWS, sendStats } from '../../../../../services/mixpanel';
import { MXP_CARD_ORDER_CONFIRM, MXP_CARD_ORDER_PAYMENT_METHOD } from '../../mixpanel-events';
import {
  CardPaymentMethods,
  cardProviders,
  mainCardTypes,
  orderCardFlowScreens,
} from '../../../constants';
import { showAlert } from '../../../../../utilities/notifications';
import { RATES_QUERY_KEY } from '../../../../../utilities/sharedQueries';
import ModalBodyLayout from '../partials/MainBodyLayout';
import { Button, Typography } from '../../../../../components';
import { useCardStatus } from '../../../../../utilities/sharedHooks/user';

const ConfirmPayment = () => {
  const { t: translate } = useTranslation();
  const history = useHistory();
  const { path } = useRouteMatch();
  const { data: rates } = useQuery(RATES_QUERY_KEY);

  const [currentWalletBalance, setCurrentWalletBalance] = useState({});
  const [balanceWithoutComma, setBalanceWithoutComma] = useState({});

  const { state, openScreen } = useContext(OrderCardContext);

  const uiFeatureShowHint = state?.cardOrderSettings?.ui?.ui_feature_show_hints;
  const ledgerSupportEmailAddress = state?.cardOrderSettings?.server?.support_email_address;
  const userWallets = state?.userWallets;
  const cardProvider = state?.cardProvider;
  const mainCardType = state?.mainCardType;
  const cardOrderFee = state?.cardOrderFee;
  const payment = state?.payment;
  const paymentCurrency = payment?.currency;
  const cardCurrency = state?.cardCurrency;
  const cardType = state?.cardType;

  const screenOptions = state?.screenOptions;
  const isCardReplacement = state?.isCardReplacement;
  const cardIssueOptions = state?.cardIssueOptions;

  const closeCardOrder = () => history.push('/dashboard/card');

  const handleClick = () => {
    if (uiFeatureShowHint) {
      history.push('/hint/card');
    }
  };

  const { refetch: refetchCardStatus } = useCardStatus({
    enabled: false,
  });

  const reportCardKey = useMemo(() => {
    if (cardIssueOptions) {
      if (cardIssueOptions?.state === REPORT_CARD_STATUS.STOLEN) {
        return CARD_MONAVATE_REPORT_STOLEN;
      }
      if (cardIssueOptions?.state === REPORT_CARD_STATUS.LOST) {
        return CARD_MONAVATE_REPORT_LOST;
      }
    }

    return null;
  }, [cardIssueOptions]);

  const { mutate: orderCardGalileo, isLoading: isLoadingGalileo } = useMutation(
    USA_CARD_CARD_ORDER,
    {
      onSettled: (data, error) => {
        sendStats(
          MXP_CARD_ORDER_CONFIRM,
          { data, error, cardProvider: cardProviders.GALILEO },
          FLOWS.CARD_ORDER
        );
        if (error) {
          showAlert({ message: error.message, type: 'danger' });
          closeCardOrder();
        } else if (data && data.success) {
          showAlert({
            message:
              mainCardType === mainCardTypes.PHYSICAL
                ? translate('us_card_request_success')
                : translate('us_virtual_card_request_success'),
            type: 'success',
            blockAutoDismiss: true,
            showIcon: true,
            onClick: handleClick,
          });
          history.push(`${path}details`);
          closeCardOrder();
        } else {
          showAlert({
            message: translate('card_order_error_msg').replace(
              '__SUPPORT_EMAIL__',
              ledgerSupportEmailAddress
            ),
            type: 'danger',
          });
          closeCardOrder();
        }
      },
    }
  );

  const { mutate: reportMastercardCard, isLoading: isLoadingReportCardMonavate } = useMutation(
    reportCardKey,
    {
      onSettled: (data, error) => {
        if (error) {
          showAlert({ message: error.message, type: 'danger' });
        } else {
          refetchCardStatus();
          closeCardOrder();
          showAlert({
            message:
              mainCardType === mainCardTypes.PHYSICAL
                ? translate('mastercard_monavate_request_success')
                : translate('mastercard_virtual_request_success'),
            type: 'success',
            blockAutoDismiss: true,
            showIcon: true,
          });
        }
      },
    }
  );

  const { mutate: orderCardMonavate, isLoading: isLoadingMonavate } = useMutation(
    CARD_ORDER_MONAVATE,
    {
      onSettled: (data, error) => {
        if (error) {
          showAlert({ message: error.message, type: 'danger' });
        } else if (data && data.success) {
          // updateCardStatus();
          showAlert({
            message:
              mainCardType === mainCardTypes.PHYSICAL
                ? translate('mastercard_monavate_request_success')
                : translate('mastercard_virtual_request_success'),
            type: 'success',
            blockAutoDismiss: true,
            showIcon: true,
          });
        } else {
          showAlert({
            message: translate('card_order_error_msg').replace(
              '__SUPPORT_EMAIL__',
              ledgerSupportEmailAddress
            ),
            type: 'danger',
          });
        }
        closeCardOrder();
      },
    }
  );

  const isLoading = useMemo(
    () => isLoadingGalileo || isLoadingMonavate || isLoadingReportCardMonavate,
    [isLoadingGalileo, isLoadingMonavate, isLoadingReportCardMonavate]
  );

  useEffect(() => {
    const wallet = userWallets.find((w) => w.coin.toUpperCase() === paymentCurrency?.toUpperCase());
    const currentCurrency = currencies[paymentCurrency?.toUpperCase()];
    setCurrentWalletBalance(
      formatNumberWithComma(
        formatNumber(wallet?.available_balance * currentCurrency?.conversion, wallet?.decimal)
      )
    );
    setBalanceWithoutComma(
      formatNumber(wallet?.available_balance * currentCurrency?.conversion, wallet?.decimal)
    );
  }, [userWallets, paymentCurrency]);

  const cardOrderFeeForDisplay = useMemo(() => {
    const cardFee = formatNumber(
      cardOrderFee ? cardOrderFee / 100 : 0,
      currencies[cardCurrency?.coin.toLowerCase()].decimal
    );
    return cardFee;
  }, [cardOrderFee, cardCurrency?.coin]);

  const getRateExchange = () => {
    const { ask } =
      rates[`${state?.payment?.currency.toUpperCase()}${cardCurrency?.coin.toUpperCase()}`] || {};
    if (ask) {
      const rate = new BigNumber(1).dividedBy(ask);
      return `1 ${cardCurrency?.coin.toUpperCase()} = ${rate} ${state?.payment?.currency?.toUpperCase()}`;
    }
    return false;
  };

  const getChargedTotalInCurrency = (onlyAmount = false) => {
    const coin = state?.payment?.currency;
    const { ask } = rates[`${coin?.toUpperCase()}${cardCurrency?.coin.toUpperCase()}`] || {};
    if (ask) {
      const rate = new BigNumber(1).dividedBy(ask);

      if (rates) {
        const fiatBalance = formatNumberWithComma(
          formatNumber(rate * cardOrderFeeForDisplay, exchange.CRYPTO_VALUE)
        );
        return onlyAmount ? fiatBalance : `${fiatBalance} ${state?.payment?.currency}`;
      }
    }
    const totalFiat = formatNumberWithComma(
      formatNumber(cardOrderFeeForDisplay, exchange.FIAT_VALUE)
    );
    return onlyAmount ? totalFiat : `${totalFiat} ${cardCurrency.coin.toUpperCase()}`;
  };

  const checkIsSufficientBalance = () => {
    const cardFee = getChargedTotalInCurrency(true);
    const isSufficient = BigNumber(balanceWithoutComma).gte(cardFee);

    if (!isSufficient) {
      showAlert({
        message: translate('order_card_insufficient_fund')
          .replace('__AMOUNT__', cardFee)
          .replace('__CURRENCY__', state?.payment?.currency?.toUpperCase()),
        type: 'danger',
      });
    }
    return isSufficient;
  };

  const onConfirmPayment = () => {
    const isBalanceEnough = checkIsSufficientBalance();
    sendStats(
      MXP_CARD_ORDER_PAYMENT_METHOD,
      {
        paymentMethod: CardPaymentMethods.CRYPTO_OR_STABLE,
        currency: cardCurrency?.coin,
        isBalanceEnough,
        cardProvider: cardType,
      },
      FLOWS.CARD_ORDER
    );

    if (isBalanceEnough) {
      if (cardType === cardProviders.MONAVATE && mainCardType === mainCardTypes.PHYSICAL) {
        if (isCardReplacement && cardProvider === cardProviders.MONAVATE) {
          reportMastercardCard({
            platform: cardCurrency.coin,
            currency: payment.currency.toUpperCase(),
          });
        } else {
          orderCardMonavate({
            platform: cardCurrency.coin,
            currency: payment.currency.toUpperCase(),
          });
        }
      } else if (cardType === cardProviders.MONAVATE && mainCardType === mainCardTypes.VIRTUAL) {
        orderCardMonavate({
          platform: cardCurrency.coin,
          currency: payment.currency.toUpperCase(),
          isVirtual: true,
        });
      } else if (
        cardType === cardProviders.GALILEO &&
        mainCardType.toLowerCase() === mainCardTypes.VIRTUAL
      ) {
        orderCardGalileo({
          currency: payment.currency,
          platform: cardCurrency.coin,
          isVirtual: true,
        });
      } else if (
        cardType === cardProviders.GALILEO &&
        mainCardType.toLowerCase() === mainCardTypes.PHYSICAL
      ) {
        orderCardGalileo({ currency: payment.currency, platform: cardCurrency.coin });
      }
    }
  };

  const renderFooter = () => (
    <div className="btn-container">
      <Button
        className="w-100"
        loading={isLoading ? 'true' : 'false'}
        disabled={isLoading}
        variant="primary"
        onClick={onConfirmPayment}>
        {translate('confirm_button_label')}
      </Button>
    </div>
  );

  return (
    <ModalBodyLayout
      withBack
      onPrevClick={() => openScreen(orderCardFlowScreens.SELECT_PAYMENT, { ...screenOptions })}
      title={translate('card_order_confirm_payment_title')}>
      <div className="mt-4">
        <div className="d-flex flex-column p-0 mb-2 rounded-extra bg-lighter">
          <div className="p-3">
            <div className="d-flex flex-row justify-content-between">
              <Typography variant="small" className="text-shadow">{`${
                payment?.currency
              } ${translate('total_balance_for_coin')}`}</Typography>
              <Typography
                as="b"
                variant="smallSemi">{`${currentWalletBalance} ${payment?.currency}`}</Typography>
            </div>
          </div>
          <div className="p-3">
            <div className="d-flex flex-row justify-content-between">
              <Typography variant="small" className="text-shadow">
                {translate('card_fees_to_pay')}
              </Typography>
              <Typography as="b" variant="smallSemi">
                {`${cardOrderFeeForDisplay} ${cardCurrency.coin.toUpperCase()}`}{' '}
              </Typography>
            </div>
          </div>
          <div className="p-3">
            <div className="d-flex flex-row justify-content-between">
              <Typography variant="small" className="text-shadow">
                {translate('will_be_charged')}
              </Typography>
              <Typography as="b" variant="smallSemi">
                {getChargedTotalInCurrency()}
              </Typography>
            </div>
          </div>
          {getRateExchange() && (
            <>
              <div className="p-3">
                <div className="d-flex flex-row justify-content-between">
                  <Typography variant="small" className="text-shadow">
                    {translate('market_rate')}
                  </Typography>
                  <Typography as="b" variant="smallSemi">{`${getRateExchange()}`}</Typography>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {renderFooter()}
    </ModalBodyLayout>
  );
};

export default ConfirmPayment;
